// Static lore + metadata for boss roster + enemy codex.
//
// Kept in a plain JS module so the UI can import without a network round-trip.
// Backend `player_bestiary` collection stores the per-wallet aggregate counts;
// combining that with this static catalog gives the full gallery experience.

export const BOSS_LORE = {
  boss_dog: {
    name: 'IRON HOUND',
    levels: [1],
    tier: 'Rank I',
    color: '#FF3B30',
    description:
      'A rusted-brass guard hound assembled by a bitter clockmaker after the neon-runners stole his daughter. Its twin ruby eyes scan every alley; when it locks on, it charges. Its bark ignites — literally.',
    weakness: 'The exposed neck cabling behind the head becomes vulnerable when the hound rears back to bark.',
  },
  boss_alley: {
    name: 'CHAIN LEADER',
    levels: [2],
    tier: 'Rank I',
    color: '#FF00FF',
    description:
      'The neon-runners crowned him after he cut the last king down with a spinning chain. His mohawk still crackles from the same fuse. Every strike carves fresh graffiti into the alley walls.',
    weakness: 'The chain has a rhythm — three swings then a pause. Time your dash for the pause window.',
  },
  boss_worm: {
    name: 'DUNE DEVOURER',
    levels: [3],
    tier: 'Rank II',
    color: '#8A6B2F',
    description:
      "Older than the desert itself. It moves through sand the way a shark moves through water — you never see it until the teeth. The town that built the mesa vanished in a single afternoon.",
    weakness: 'Watch the sand mounds — they mark its underground path. It always erupts a heartbeat before biting.',
  },
  boss_highway: {
    name: 'DESERT PROWLER',
    levels: [4],
    tier: 'Rank II',
    color: '#FF9500',
    description:
      'A stolen highway patrol vehicle with a scavenged turret welded to the bed. The driver has not been seen in years but the truck still fills up at the cratered gas station every Friday.',
    weakness: 'The turret fires in bursts of three — reload window is your best opening. Aim for the driver-side window.',
  },
  boss_quarry: {
    name: 'CONCRETE COLOSSUS',
    levels: [5],
    tier: 'Rank III',
    color: '#5AC8FA',
    description:
      'A reactivated construction mech from the age when they still built roads. The AI thinks the quarry is still open. It swings a wrecking ball the size of a compact car and does not remember what a lunch break is.',
    weakness: 'The wrecking ball has apex points where it slows — dash through the arc during the return swing.',
  },
  boss_factory: {
    name: 'FACTORY FOREMAN', levels: [6], tier: 'Rank III', color: '#FF6B00',
    description: 'Ran the plant for thirty years, then the plant ran him. Now his hydraulic press-arm is welded to his shoulder and he still yells at everyone about safety violations he can no longer enforce.',
    weakness: 'The pneumatic hoses under the press arm — hit them when he winds up for a slam and the arm droops.',
  },
  boss_bridge: {
    name: 'BRIDGE TROLL', levels: [7], tier: 'Rank IV', color: '#4A6B2F',
    description: 'The oldest thing in the gorge. Grew from a stump into a legend, and back into something meaner than a stump. Charges a toll in teeth.',
    weakness: 'Slow to turn — dash between his legs and hit the base of his spine before he pivots.',
  },
  boss_kraken: {
    name: 'DOCK KRAKEN', levels: [8], tier: 'Rank IV', color: '#4A6B7C',
    description: 'Came up from a fissure the last storm opened. Six tentacles. One yellow eye. Every fisherman in town owes the sea a boat and this is how it collects.',
    weakness: 'The central eye is armored except during the roar animation — headshot window is short but merciful.',
  },
  boss_efreet: {
    name: 'VOLCANIC EFREET', levels: [9], tier: 'Rank V', color: '#FF4500',
    description: 'Dredged up from the caldera by the neon-runners as a warning. It burned the warning down. Now it just wanders.',
    weakness: 'Freeze / ice power-ups halve its speed and expose the obsidian core at its chest.',
  },
  boss_runner: {
    name: 'NEON RUNNER PRIME', levels: [10], tier: 'Rank V', color: '#00FFFF',
    description: 'The first of the neon-runners, upgraded until there is nothing organic left. Dual pistols, holo-visor, LED trails. Believes he is the protagonist of his own game.',
    weakness: 'Predictable dash pattern — count to three then punish the pause. Removes his own visor at phase 3.',
  },
  boss_wraith: {
    name: 'ARCTIC WRAITH', levels: [11], tier: 'Rank V', color: '#B0E0E6',
    description: 'The tundra remembers everyone who died crossing it. She is what happens when the memories start walking.',
    weakness: 'Fire-based power-ups melt her ice shards. The face beneath the hood is her mortal form.',
  },
  boss_yeti: {
    name: 'SNOW YETI', levels: [12], tier: 'Rank V', color: '#F0F0F0',
    description: 'Not a myth. Always was there. Has been eating climbers since before your grandfather owned a rifle.',
    weakness: 'The white fur is not armor — it just hides where the wounds are. Aim center-mass and keep firing.',
  },
  boss_vine: {
    name: 'JUNGLE VINE KING', levels: [13], tier: 'Rank VI', color: '#7CFC00',
    description: 'The jungle grew a king because it had run out of good ideas. Every step regrows a village that should still be here.',
    weakness: 'Fire burns through his vines like paper — carry fire power-ups into this fight.',
  },
  boss_toxin: {
    name: 'SWAMP TOXIN', levels: [14], tier: 'Rank VI', color: '#9ACD32',
    description: 'Once a chemist for the resource wars, then his tanks ruptured and he became his own experiment. His footprints kill grass for a decade.',
    weakness: 'The tanks on his back are pressurized — pop one and the second becomes a bomb on his shoulders.',
  },
  boss_sentinel: {
    name: 'STATION SENTINEL', levels: [15], tier: 'Rank VI', color: '#FF0000',
    description: 'Four-legged automated guardian of a factory whose blueprints have been lost since the war. Its searchlight is what nightmares use for a nightlight.',
    weakness: 'The searchlight has a blind spot directly under its chassis — dash beneath and unload.',
  },
  boss_cosmic: {
    name: 'COSMIC WARLORD', levels: [16], tier: 'Rank VII', color: '#B0B0FF',
    description: 'Came through the station airlock in a suit that fits like it grew inside him. Carries a staff that predates the concept of "star." Impatient with slow species.',
    weakness: 'The staff orb is a battery — three focused hits crack it and the shields go with it.',
  },
  boss_castle: {
    name: 'CASTLE WRAITH', levels: [17], tier: 'Rank VII', color: '#FFD700',
    description: "Wore that armor to his own coronation. Never took it off. The visor still glows because whatever he became needs to see who he's cutting down.",
    weakness: "The greatsword's arc is enormous but wide-open on the recovery. Dash-cancel his upswing.",
  },
  boss_phoenix: {
    name: 'RUIN PHOENIX', levels: [18], tier: 'Rank VII', color: '#FF6347',
    description: 'What rose out of the ashes when the city fell for the third time. Every feather is a story about someone who is not here anymore.',
    weakness: 'Water / ice power-ups extinguish its flame plumage — striking the naked body then triples damage taken.',
  },
  boss_crystal: {
    name: 'CRYSTAL GUARDIAN', levels: [19], tier: 'Rank VIII', color: '#FF00FF',
    description: 'Was born down here. Never seen sunlight. Refracts every hostile intent into a beam it fires back with interest.',
    weakness: 'The heart gem in its chest is exposed after every 3-attack cycle — that is your kill window.',
  },
  boss_omega: {
    name: 'SHADOW OMEGA', levels: [20], tier: 'Final', color: '#FFD700',
    description: 'The endpoint. The thing that has been pulling the strings across every level. Six eyes. Eight tentacles. Remembers your first death, your worst dodge, and your favourite gun.',
    weakness: 'Unknown. Bring everything.',
  },
};

// Enemy codex — one entry per enemy type. Level-palette variants are shown
// dynamically in the UI based on which levels the player has reached.
export const ENEMY_LORE = {
  grunt: {
    name: 'Grunt',
    tier: 'Common',
    description: 'The rank-and-file. Slow, cheap, mass-produced. They pour in from off-screen and rarely think.',
  },
  soldier: {
    name: 'Soldier',
    tier: 'Common',
    description: 'Better trained than a Grunt. Actually aims. Sometimes hops small obstacles.',
  },
  runner: {
    name: 'Runner',
    tier: 'Common',
    description: 'Sprinter caste. Closes distance fast, dies fast. Ignores fire discipline.',
  },
  jumper: {
    name: 'Jumper',
    tier: 'Uncommon',
    description: 'Assault trooper with jump boots. Loves height advantage. Will land on your head if allowed.',
  },
  tank: {
    name: 'Tank',
    tier: 'Uncommon',
    description: 'Tracked chassis, main cannon, no manners. Absorbs an obscene amount of damage.',
  },
  commando: {
    name: 'Commando',
    tier: 'Rare',
    description: 'Elite operator. Fast, aggressive, high fire-rate. Purple beret means "priority target."',
  },
  ninja: {
    name: 'Ninja',
    tier: 'Rare',
    description: 'Silent, quick, deadly. Red headband. Will close the gap in under a second if you blink.',
  },
  berserker: {
    name: 'Berserker',
    tier: 'Rare',
    description: 'All-in melee-obsessed nightmare with three red spikes on the head. Zero fear response.',
  },
  elite: {
    name: 'Elite Guard',
    tier: 'Elite',
    description: 'Golden-armored heavy infantry. Long health bar, precise fire, worth 2× score.',
  },
  drone: {
    name: 'Drone',
    tier: 'Uncommon',
    description: 'Autonomous aerial scout. Fast, fragile, dangerous in swarms. Rotating rotor blade.',
  },
  helicopter: {
    name: 'Attack Helicopter',
    tier: 'Rare',
    description: 'Rotor-wing gunship. Hovers just out of jump range and rains bullets from above.',
  },
  gunship: {
    name: 'Assault Gunship',
    tier: 'Elite',
    description: 'Twin-rotor heavy air support. Massive damage output, harder to shoot down than a helicopter.',
  },
  mech: {
    name: 'Mech',
    tier: 'Elite',
    description: 'Two-legged battle-suit piloted by whoever drew the short straw. Slow but relentless.',
  },
  sniper: {
    name: 'Sniper',
    tier: 'Elite',
    description: 'Long-range specialist. Halts, aims (red laser sight telegraph), fires one crippling shot. Do not stand still.',
    isNew: true,
  },
  teleporter: {
    name: 'Teleporter',
    tier: 'Elite',
    description: 'Warped-in from somewhere darker. Fades out every 2-3 seconds and reappears 200px closer. Purple rune-orb above head.',
    isNew: true,
  },
  brute: {
    name: 'Brute',
    tier: 'Elite',
    description: 'Charging heavy-hitter. Telegraphs with a red glow, then dashes at 3× speed. Massive shoulder pads. Get out of the lane.',
    isNew: true,
  },
};

// Ordered array — useful for grid layouts that want stable rendering order.
// Order = ascending level of first appearance (L1 → L20).
export const BOSS_ORDER = [
  'boss_dog', 'boss_alley', 'boss_worm', 'boss_highway', 'boss_quarry',
  'boss_factory', 'boss_bridge', 'boss_kraken', 'boss_efreet', 'boss_runner',
  'boss_wraith', 'boss_yeti', 'boss_vine', 'boss_toxin', 'boss_sentinel',
  'boss_cosmic', 'boss_castle', 'boss_phoenix', 'boss_crystal', 'boss_omega',
];
export const ENEMY_ORDER = [
  'grunt', 'soldier', 'runner', 'jumper', 'tank',
  'commando', 'ninja', 'berserker', 'elite',
  'drone', 'helicopter', 'gunship', 'mech',
  'sniper', 'teleporter', 'brute',
];
